import { ReactNode } from 'react';
import DOMPurify from 'dompurify';
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import PDFViewer from 'src/components/pdf-viewer/PDFViewer';
import { XlsxViewer } from '@/components/xlsx-viewer/XlsxViewer';
import { ImageWithSmartScaling } from '@/components/common/ImageWithSmartScaling';
import { isExcelFile } from './utils';
import type { EmailAttachment, DocumentAssociation } from '@erp/rpc';

/**
 * Builds a map of content IDs to signed URLs for inline email images
 * @param documentAssociations - Document associations that may contain inline images
 * @returns Map of content ID to signed URL
 */
export function buildCidMap(
  documentAssociations?: Array<{
    document: {
      contentId?: string | null;
      url: string;
    };
  }>,
): Map<string, string> {
  const cidMap = new Map<string, string>();

  if (!documentAssociations) return cidMap;

  for (const association of documentAssociations) {
    const { document } = association;
    if (document.contentId && document.url) {
      // Store without angle brackets if present (some email clients include them)
      let cleanContentId = document.contentId.replace(/^<|>$/g, '');
      // Strip @... suffix to match how the regex extracts the contentId
      // (e.g., "image001.png@01234567.ABCDEF" -> "image001.png")
      const atIndex = cleanContentId.indexOf('@');
      if (atIndex !== -1) {
        cleanContentId = cleanContentId.substring(0, atIndex);
      }
      cidMap.set(cleanContentId, document.url);
    }
  }

  return cidMap;
}

/**
 * Replaces cid: URLs in HTML content with actual signed URLs
 * @param content - HTML content with potential cid: references
 * @param cidMap - Map of content IDs to signed URLs
 * @returns HTML content with cid: URLs replaced
 */
function replaceCidUrls(content: string, cidMap: Map<string, string>): string {
  if (cidMap.size === 0) return content;

  // Replace cid: references in src attributes
  // Handles both cid:image001.png and cid:image001.png@01234567.ABCDEF formats
  return content.replace(
    /src=["']cid:([^"'@>]+)(?:@[^"'>]*)?\s*["']/gi,
    (match, contentId) => {
      const url = cidMap.get(contentId);
      if (url) {
        return `src="${url}"`;
      }
      // Return original if no mapping found
      return match;
    },
  );
}

export function renderEmailBody(
  content?: string | null,
  cidMap?: Map<string, string>,
): ReactNode {
  if (!content) return null;

  const processedContent = content;

  const hasContent =
    processedContent
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim().length > 0;

  const contentWithResolvedCids = cidMap
    ? replaceCidUrls(processedContent, cidMap)
    : processedContent;

  // Sanitize HTML content to prevent XSS attacks
  const sanitizedContent = DOMPurify.sanitize(contentWithResolvedCids, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'em',
      'u',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'a',
      'img',
      'blockquote',
      'pre',
      'code',
      'div',
      'span',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id', 'style'],
    ALLOW_DATA_ATTR: false,
  });

  return hasContent ? (
    <div
      className="prose prose-sm max-w-none
            [&_img]:max-w-full
            [&_a]:text-primary [&_a]:underline
            [&_p]:mb-4 [&_p]:leading-relaxed [&_p]:text-sm
            [&_h1]:mb-2 [&_h2]:mb-2 [&_h3]:mb-2 [&_h4]:mb-2 [&_h5]:mb-2 [&_h6]:mb-2
            [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:pl-6 [&_ol]:mb-4
            [&_blockquote]:border-l-4 [&_blockquote]:border-muted [&_blockquote]:pl-4 [&_blockquote]:ml-0 [&_blockquote]:mb-4 [&_blockquote]:italic
            [&_pre]:bg-muted [&_pre]:p-4 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:mb-4
            [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm
            overflow-x-auto"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: Content is properly sanitized with DOMPurify before rendering
      dangerouslySetInnerHTML={{
        __html: sanitizedContent,
      }}
    />
  ) : (
    <div className="flex items-center justify-center p-16 border border-dashed rounded-lg">
      <p className="text-sm text-muted-foreground italic opacity-50 tracking-wide">
        No email body content
      </p>
    </div>
  );
}

/**
 * Renders email attachments with proper viewers
 * @param attachments - Legacy email attachments
 * @param documentAssociations - Document associations (new system)
 * @param highlightedDocumentIds - Document IDs to highlight (for showing source documents)
 */
export function renderEmailAttachments(
  attachments?: EmailAttachment[],
  documentAssociations?: DocumentAssociation[],
  highlightedDocumentIds: string[] = [],
): ReactNode {
  const isPdf = (fileName: string, mimeType?: string) =>
    mimeType === 'application/pdf' || fileName?.toLowerCase().endsWith('.pdf');

  const allAttachments = [
    ...(attachments || []).map((att) => ({
      id: att.id,
      documentId: undefined, // Legacy attachments don't have document IDs
      fileName: att.fileName,
      sizeBytes: att.sizeBytes,
      mimeType: att.mimeType,
      s3Url: att.s3Url,
    })),
    ...(documentAssociations || []).map((doc) => ({
      id: doc.id,
      documentId: doc.document.id, // Use the actual document ID for highlighting
      fileName: doc.document.originalFileName || doc.document.name,
      sizeBytes: doc.document.fileSizeBytes || 0,
      mimeType: doc.document.mimeType || undefined,
      s3Url: doc.document.url,
    })),
  ];

  if (!allAttachments.length) return null;

  // Sort PDFs first
  const sortedAttachments = allAttachments.sort((a, b) => {
    const aIsPdf = isPdf(a.fileName, a.mimeType);
    const bIsPdf = isPdf(b.fileName, b.mimeType);
    if (aIsPdf && !bIsPdf) return -1;
    if (!aIsPdf && bIsPdf) return 1;
    return 0;
  });

  return (
    <div className="space-y-4">
      {sortedAttachments.map((attachment) => {
        const isHighlighted =
          attachment.documentId &&
          highlightedDocumentIds.includes(attachment.documentId);

        return (
          <div
            key={attachment.id}
            className={`space-y-2 p-2 rounded-md ${
              isHighlighted ? 'border-2 border-primary bg-primary/5' : ''
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h6 className="text-sm font-medium break-words">
                {isHighlighted && <span className="text-primary mr-1">●</span>}
                {attachment.fileName} (
                {(attachment.sizeBytes / 1024).toFixed(2)} KB)
              </h6>
              <Button
                variant="link"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(attachment.s3Url, '_blank');
                }}
                className="h-auto p-1"
              >
                <ArrowSquareOutIcon className="w-4 h-4" />
              </Button>
            </div>
            {attachment.mimeType?.startsWith('image/') ? (
              <ImageWithSmartScaling
                src={attachment.s3Url}
                alt={attachment.fileName}
                fileName={attachment.fileName}
                sizeBytes={attachment.sizeBytes}
              />
            ) : isPdf(attachment.fileName, attachment.mimeType) ? (
              <PDFViewer url={attachment.s3Url} />
            ) : isExcelFile({
                fileName: attachment.fileName,
                mimeType: attachment.mimeType,
              }) ? (
              <XlsxViewer
                url={attachment.s3Url}
                fileName={attachment.fileName}
              />
            ) : (
              <a
                href={attachment.s3Url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline text-sm hover:text-primary/80"
                onClick={(e) => e.stopPropagation()}
              >
                Download File
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}
